import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { BaseLineUsed } from '@/app/components/icons/BaseLineUsed';
import { ProductLineApply1 } from '@/app/components/icons/ProductLineApply1';
import type { FeedCardProps } from '@/app/components/shared/Community/components/FeedCard';
import CountButton from '@/app/components/shared/CountButton';
import { ImageApplyButton } from '@/app/components/shared/ImageApplyButton';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import LikeButton from '@/app/components/shared/LikeButton';
import { Medal } from '@/app/components/shared/Medal';
import { Button } from '@/app/components/ui/button';
import { TRACE_EVENT_ID, TRACE_EVENT_NAME } from '@/app/constants';
import { useGoToProfile } from '@/app/hooks';
import { BackfillTaskType, CommunityEnum } from '@/app/types';
import { cn, getItemType, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import type { MediaAssetItem, MiniVideoItem, ShortFilmItem, WorkItem } from '../types';
import { getAverageColor } from '../utils';
import { FeedTag } from './FeedTag';

type HoverContentItem = MediaAssetItem | ShortFilmItem | MiniVideoItem | WorkItem;

/** 判断是否为活动作品 */
const isWork = (item?: HoverContentItem): item is WorkItem => {
  return !!item && 'activity_id' in item;
};

interface HoverContentProps extends Partial<FeedCardProps> {
  currentItem?: HoverContentItem;
  className?: string;
}

export const HoverContent: FC<HoverContentProps> = ({
  currentItem,
  item,
  avatar,
  nickName,
  medals = [],
  info,
  hideEmptyVoteCount,
  handleDetail,
  handleVote,
  handleUnVote,
  actions,
  className,
  tag,
  tooltipContent,
}) => {
  const { goToProfile } = useGoToProfile();
  const [averageColor, setAverageColor] = useState<string>('rgba(0, 0, 0, 0.64)');
  const containerRef = useRef<HTMLDivElement>(null);
  const hasFetchedColorRef = useRef(false);
  const pathname = usePathname();
  const itemData = useMemo(() => {
    if (isWork(currentItem)) {
      return {
        id: currentItem.id || '',
        creatorId: currentItem.creator?.id,
        coverUri: currentItem.creation?.cover_uri,
        avatar: avatar || currentItem.creator?.avatar,
        nickName: nickName || currentItem.creator?.nick_name,
        voted: currentItem.liked ?? false,
        voteCount: Number(currentItem.like_num) || 0,
        recreateCount: 0,
        traceId: item?.traceId || currentItem.id || '',
        type: currentItem.type,
      };
    }
    return {
      id: currentItem?.id || '',
      creatorId: currentItem?.creator?.id,
      coverUri: currentItem?.creation?.cover_uri,
      avatar,
      nickName,
      voted: currentItem?.react_info?.voted || false,
      voteCount: currentItem?.react_info?.vote_count || 0,
      recreateCount: (currentItem as MediaAssetItem)?.recreate_count ?? 0,
      traceId: item?.traceId || '',
      type: undefined,
    };
  }, [currentItem, avatar, nickName, item?.traceId]);

  // 只在可视范围内获取封面颜色
  useEffect(() => {
    const element = containerRef.current;
    const coverUri = itemData.coverUri;
    if (!element || !coverUri) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !hasFetchedColorRef.current) {
          hasFetchedColorRef.current = true;
          getAverageColor(coverUri).then((data) => {
            if (data.success && data.color) {
              setAverageColor(data.color);
            }
          });
          observer.disconnect();
        }
      },
      { rootMargin: '100px' },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [itemData.coverUri]);

  return (
    <>
      <div
        ref={containerRef}
        className={cn(
          'absolute right-0 bottom-0 left-0 z-10 rounded-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100 sm:hidden',
          className,
        )}
      >
        <div
          className={cn(
            'pointer-events-none absolute inset-0 z-0 bg-gradient-to-b from-transparent to-[var(--average-color)]',
            !actions && 'rounded-b-4',
          )}
          style={{ '--average-color': averageColor } as React.CSSProperties}
        />
        <div className="relative flex justify-between gap-6 p-4 pt-[30px]">
          <div className="line-clamp-1 flex flex-1 items-center gap-2 truncate">
            <div className="flex cursor-pointer items-center gap-2" onClick={() => goToProfile(itemData.creatorId)}>
              {itemData.avatar && (
                <ImageWithPlaceholder
                  src={itemData.avatar}
                  alt="avatar"
                  className="h-4 w-4 rounded-full object-cover sm:h-3.5 sm:w-3.5"
                />
              )}
              <div className={cn('max-w-14 truncate text-xs sm:max-w-14', !info && 'max-w-20 sm:max-w-14')}>
                {itemData.nickName}
              </div>
            </div>
            {medals.length > 0 && (
              <Medal medals={medals} size="small" onClick={() => handleDetail?.(itemData.id, itemData.traceId)} />
            )}
          </div>
          <div className="flex flex-shrink-0 items-center gap-3">
            <div className="text-sm sm:hidden">{info}</div>
            {info && <div className="h-3 w-[1px] bg-system-white48 sm:hidden" />}
            {itemData.recreateCount > 0 && (
              <CountButton
                count={itemData.recreateCount}
                numClassName="text-xs font-normal"
                activeIcon={<BaseLineUsed className="text-sm" />}
                unActiveIcon={<BaseLineUsed className="text-sm" />}
                enableClick={false}
              />
            )}
            <LikeButton
              id={itemData.id}
              itemVote={itemData.voted}
              tabType={
                itemData.type ? getItemType({ type: itemData.type } as MediaAssetItem) : getItemType(currentItem)
              }
              itemVoteCount={itemData.voteCount}
              hideEmptyVoteCount={hideEmptyVoteCount}
              traceId={itemData.traceId}
              handleVote={handleVote}
              handleUnVote={handleUnVote}
              iconClassName="text-sm"
              numClassName="text-xs font-normal"
            />
          </div>
        </div>

        {actions && actions.length > 0 && (
          <div
            className="absolute hidden w-full flex-row-reverse items-center justify-center gap-2 rounded-b-4 p-4 group-hover:flex"
            style={{ backgroundColor: averageColor }}
          >
            {actions.map((action) => {
              if (action.key === 'apply') {
                return (
                  <ImageApplyButton
                    key={action.key}
                    inspirationId={itemData.id}
                    directApplyType={BackfillTaskType.CHARACTER2VIDEO}
                    content={
                      <Button
                        className="size-8 rounded-8 bg-system-white10 p-0 hover:bg-system-white/20"
                        onClick={(e) => {
                          e.stopPropagation();
                          action.onClick?.();
                        }}
                      >
                        <ProductLineApply1 className="text-base" />
                      </Button>
                    }
                    onApplyToTool={() => {
                      reportEvent(TRACE_EVENT_NAME[CommunityEnum.ImageAsset], {
                        [TRACE_EVENT_ID[CommunityEnum.ImageAsset]]: itemData.id,
                        interaction_type: 'apply',
                        interaction_position: pathname,
                        content_type: 'image',
                      });
                    }}
                  />
                );
              }
              return (
                <Button
                  className="h-8 flex-1 bg-system-white10 text-sm hover:bg-system-white/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick?.();
                  }}
                  key={action.key}
                >
                  {action.title}
                </Button>
              );
            })}
          </div>
        )}
      </div>
      {tag && (
        <FeedTag text={tag} className="absolute top-2 left-2 cursor-help sm:hidden" tooltipContent={tooltipContent} />
      )}
    </>
  );
};
